import type {
  IProcessItemDetailModel,
  IProcessItemDetailViewModel,
  IProcessDetailResponse,
  IProcessDetailViewModel,
} from "@models/extraction";

export const buildProcessItemDetailViewModel = (
  item: IProcessItemDetailModel,
): IProcessItemDetailViewModel => ({
  id: item.id,
  idShort: item.id.substring(0, 8),
  fileName: item.file_name,
  status: item.status,
  errorMessage: item.error_message,
  createdAt: item.created_at,
});

/**
 * Normalise `user_input.page_numbers` into a `number[]`.
 *
 * The backend has returned this field as several shapes over time — a
 * comma-separated string ("1,2,3"), a real array ([1, 2, 3] or ["1", "2"]),
 * a single number, or null/undefined. The previous `(x ?? "").split(",")`
 * assumed a string and threw `.split is not a function` on the array shape.
 */
const normalizePageNumbers = (raw: unknown): number[] => {
  const toInt = (v: unknown): number => parseInt(String(v).trim(), 10);
  const values: unknown[] = Array.isArray(raw)
    ? raw
    : raw === null || raw === undefined || raw === ""
      ? []
      : String(raw).split(",");
  return values.map(toInt).filter((n) => !Number.isNaN(n));
};

export const buildProcessDetailViewModel = (
  response: IProcessDetailResponse,
): IProcessDetailViewModel => ({
  id: response.id,
  status: response.status,
  progress: {
    total: response.progress.total,
    completed: response.progress.completed,
  },
  isBatch: response.is_batch,
  startAt: response.start_at,
  finishedAt: response.finished_at,
  errorMessage: response.error_message,
  userInput: {
    pageNumbers: normalizePageNumbers(response.user_input.page_numbers),
    instructions: response.user_input.instructions ?? "",
  },
  processItems: response.process_items.map(buildProcessItemDetailViewModel),
});
